import { FormControl, FormField, FormItem } from '@/components/primitives/Form';
import { useFormField } from '@/components/primitives/useFormField';
import { cn } from '@/utils/cn';
import { Input } from '@packages/webshop-design-system';
import _kebabCase from 'lodash/kebabCase';
import React from 'react';
import {
  useFormContext,
  type ControllerRenderProps,
  type FieldValues,
  type Path,
} from 'react-hook-form';

interface InputFieldContentProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<InputFieldProps<TFieldValues, TName>, 'name'> {
  field: ControllerRenderProps<TFieldValues, TName>;
}

const InputFieldContent = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  field,
  label,
  hideLabel,
  endAdornment,
  classNames,
  required,
  ...props
}: InputFieldContentProps<TFieldValues, TName>) => {
  const { error } = useFormField();

  return (
    <FormItem className={classNames?.wrapper}>
      <FormControl label={label} required={required} hideLabel={hideLabel}>
        <Input
          {...field}
          {...props}
          data-testid={`${_kebabCase(field.name)}-input-field`}
          classNames={{
            wrapper: cn(classNames?.input),
            input: cn({
              ['border-error-text hover:border-error-text focus-visible:border-error-text focus-visible:ring-error-text']:
                error,
            }),
          }}
          aria-invalid={!!error}
          {...(endAdornment && { endAdornment })}
        />
      </FormControl>
    </FormItem>
  );
};

export interface InputFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
> extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'name' | 'className'
  > {
  label: React.ReactNode;
  name: TName;
  hideLabel?: boolean;
  endAdornment?: React.ReactNode;
  classNames?: { wrapper?: string; input?: string };
}

export const InputField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends Path<TFieldValues> = Path<TFieldValues>,
>({
  name,
  ...props
}: InputFieldProps<TFieldValues, TName>) => {
  const { control } = useFormContext<TFieldValues>();

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => <InputFieldContent field={field} {...props} />}
    />
  );
};

InputField.displayName = 'InputField';
